
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Provider {
  id: string;
  name: string;
  description: string;
  specialties: string[];
  rating: number;
  review_count: number;
  location: string;
  address: string;
  phone?: string;
  email?: string;
  website?: string;
  image_url?: string;
  slug: string;
  stripe_connect_account_id?: string;
  provider_contact_email?: string;
  email_notifications_enabled?: boolean;
  latitude?: number;
  longitude?: number;
  commission_fee_percent?: number;
  check_in_window_minutes?: number;
  early_access_enabled?: boolean;
  early_access_minutes?: number;
  min_booking_advance_minutes?: number;
  brand_primary_color?: string | null;
  brand_secondary_color?: string | null;
  brand_accent_color?: string | null;
  brand_font?: string | null;
  brand_heading_color?: string | null;
  brand_body_color?: string | null;
  brand_background_color?: string | null;
  is_hidden?: boolean;
  created_at: string;
  updated_at: string;
}

export const useProviders = () => {
  return useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      // Fetch providers
      const { data: providersData, error: providersError } = await supabase
        .from('providers')
        .select('*');

      if (providersError) {
        console.error('Error fetching providers:', providersError);
        throw providersError;
      }

      // Fetch all reviews to calculate real-time ratings and counts
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select('provider_id, rating');

      if (reviewsError) {
        console.error('Error fetching reviews:', reviewsError);
        throw reviewsError;
      }

      // Calculate review counts and average ratings per provider
      const reviewStats = reviewsData?.reduce((acc, review) => {
        if (!acc[review.provider_id]) {
          acc[review.provider_id] = { totalRating: 0, count: 0 };
        }
        acc[review.provider_id].totalRating += review.rating;
        acc[review.provider_id].count += 1;
        return acc;
      }, {} as Record<string, { totalRating: number; count: number }>) || {};

      // Merge providers with calculated review stats
      const providersWithRealReviews = providersData?.map(provider => {
        const stats = reviewStats[provider.id] || { totalRating: 0, count: 0 };
        const avgRating = stats.count > 0 
          ? parseFloat((stats.totalRating / stats.count).toFixed(1))
          : 0;
        
        return {
          ...provider,
          rating: avgRating,
          review_count: stats.count
        };
      }) || [];

      // Filter out hidden providers for public display
      const visibleProviders = providersWithRealReviews.filter(p => !p.is_hidden);

      // Sort by rating (highest first)
      visibleProviders.sort((a, b) => b.rating - a.rating);

      return visibleProviders as Provider[];
    },
  });
};

export const useProvider = (slug: string) => {
  return useQuery({
    queryKey: ['provider', slug],
    queryFn: async () => {
      console.log('Fetching provider with slug:', slug);
      
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('slug', slug)
        .maybeSingle(); // Use maybeSingle instead of single to handle no results gracefully

      if (error) {
        console.error('Error fetching provider:', error);
        throw error;
      }

      console.log('Provider data received:', data);
      return data as Provider | null;
    },
    enabled: !!slug,
  });
};

export const useProviderById = (id: string) => {
  return useQuery({
    queryKey: ['provider', 'by-id', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('providers')
        .select('*')
        .eq('id', id)
        .maybeSingle(); // Use maybeSingle instead of single

      if (error) {
        console.error('Error fetching provider by ID:', error);
        throw error;
      }

      return data as Provider | null;
    },
    enabled: !!id,
  });
};
